import { Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput } from "@mantine/core";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import {
  EMPTY_DOOR,
  useSaveDoor,
  useSetDoorActive,
  validateDoor,
  type DoorFields,
  type ManagedDoor,
} from "./door-queries";

export interface DoorModalProps {
  /** null to add a door, a row to edit it; undefined when closed. */
  readonly door: ManagedDoor | null | undefined;
  readonly otherNames: readonly string[];
  /** Active campuses; the campus box shows only when the church has more than one. */
  readonly campuses: readonly { value: string; label: string }[];
  readonly onClose: () => void;
}

/** One dialog for adding and editing a door, remounted per door by the page's key. The same shape as a room's. */
export function DoorModal({ door, otherNames, campuses, onClose }: DoorModalProps) {
  const save = useSaveDoor();
  const setActive = useSetDoorActive();
  const [fields, setFields] = useState<DoorFields>(
    door === null || door === undefined
      ? EMPTY_DOOR
      : { name: door.name, campusId: door.campusId, sortOrder: door.sortOrder },
  );
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const problems = validateDoor(fields, otherNames);
  const invalid = Object.keys(problems).length > 0;
  const shown = touched ? problems : {};

  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another door already has that name."
      : code === "42501"
        ? "You need permission to manage events to change doors."
        : save.isError
          ? "The door was not saved. Try again."
          : null;

  return (
    <>
      <Modal
        opened={door !== undefined}
        onClose={onClose}
        title={door === null ? "Add a door" : "Edit door"}
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            description='Where this station stands, such as "Main entrance" or "Kids wing".'
            required
            error={shown.name}
            value={fields.name}
            onChange={(event) => {
              const name = event.currentTarget.value;
              setFields((current) => ({ ...current, name }));
            }}
          />
          {campuses.length > 1 ? (
            <Select
              label="Campus"
              description="Leave empty for a door at any campus."
              clearable
              data={[...campuses]}
              value={fields.campusId}
              onChange={(campusId) => {
                setFields((current) => ({ ...current, campusId }));
              }}
            />
          ) : null}
          <NumberInput
            label="Order"
            description="Lower comes first at the station."
            min={0}
            max={999}
            allowDecimal={false}
            value={fields.sortOrder}
            onChange={(value) => {
              setFields((current) => ({
                ...current,
                sortOrder: typeof value === "number" ? value : 0,
              }));
            }}
          />
          <Text size="xs" c="dimmed">
            The station asks which door it is at and shows the dates at that door&apos;s campus.
            Every door on the same date shares one session.
          </Text>
          {errorText !== null ? (
            <Text size="sm" c="red" role="alert">
              {errorText}
            </Text>
          ) : null}
          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {door !== null && door !== undefined ? (
                door.isActive ? (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setConfirming(true);
                    }}
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    loading={setActive.isPending}
                    onClick={() => {
                      setActive.mutate({ doorId: door.id, isActive: true }, { onSuccess: onClose });
                    }}
                  >
                    Restore
                  </Button>
                )
              ) : null}
            </Group>
            <Button
              loading={save.isPending}
              onClick={() => {
                setTouched(true);
                if (invalid) return;
                save.mutate({ doorId: door?.id ?? null, fields }, { onSuccess: onClose });
              }}
            >
              {door === null ? "Add door" : "Save door"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Archive door"
        message={
          door === null || door === undefined
            ? ""
            : `Archive ${door.name}? The station stops offering it. Nothing already checked in changes, and you can restore it later.`
        }
        confirmLabel="Archive door"
        danger
        loading={setActive.isPending}
        onConfirm={() => {
          if (door === null || door === undefined) return;
          setActive.mutate(
            { doorId: door.id, isActive: false },
            {
              onSuccess: () => {
                setConfirming(false);
                onClose();
              },
            },
          );
        }}
      />
    </>
  );
}
